/**
 * Helper functions to safely access environment variables
 * This helps with Vite's special handling of environment variables
 */

// Define interface for window with __ENV property
interface WindowWithEnv extends Window {
  __ENV?: Record<string, string>;
}

// Get environment variables with appropriate fallbacks
export const getEnv = (key: string, fallback: string = ''): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // Handle Vite environment
    return (import.meta.env[key] as string) || fallback;
  } else if (typeof process !== 'undefined' && process.env) {
    // Handle Node.js environment
    return process.env[key] || fallback;
  } else if (typeof window !== 'undefined') {
    // Handle environment with injected variables
    const windowWithEnv = window as WindowWithEnv;
    return windowWithEnv.__ENV?.[key] || fallback;
  }
  return fallback;
};

// Placeholder or invalid PostHog keys – do not initialize (use .env for real key)
const POSTHOG_PLACEHOLDERS = [
  '',
  'your_posthog_api_key_here',
  'xxx',
  'phc_xxx',
  'posthog_api_key',
];
const isValidPostHogKey = (key: string | undefined): boolean => {
  if (!key || typeof key !== 'string') return false;
  const trimmed = key.trim();
  if (POSTHOG_PLACEHOLDERS.some((p) => trimmed === p || trimmed.toLowerCase().includes('your_posthog'))) return false;
  // Real PostHog project API keys are long and start with phc_
  return trimmed.length > 20 && trimmed.startsWith('phc_');
};

// Get PostHog configuration
export const getPostHogConfig = () => {
  // Prioritize the recommended environment variable names (from .env)
  const rawKey =
    getEnv('VITE_PUBLIC_POSTHOG_KEY') ||
    getEnv('VITE_POSTHOG_API_KEY') ||
    getEnv('POSTHOG_API_KEY') ||
    getEnv('REACT_APP_POSTHOG_API_KEY');
  const apiKey = isValidPostHogKey(rawKey) ? rawKey!.trim() : undefined;

  const apiHost =
    getEnv('VITE_PUBLIC_POSTHOG_HOST') ||
    getEnv('VITE_POSTHOG_HOST') ||
    getEnv('POSTHOG_HOST') ||
    getEnv('REACT_APP_POSTHOG_HOST') ||
    'https://app.posthog.com';

  return {
    apiKey: apiKey ?? '',
    apiHost,
    hasKey: !!apiKey,
    debug: {
      checked: [
        'VITE_PUBLIC_POSTHOG_KEY',
        'VITE_POSTHOG_API_KEY',
        'POSTHOG_API_KEY',
        'REACT_APP_POSTHOG_API_KEY'
      ],
      apiKeyExists: !!apiKey,
      apiKeyLength: apiKey?.length || 0,
    }
  };
}; 